// Per-repo detail data layer (#195). Point lookups + lists for the tabbed
// repo workspace, keyed by the route's percent-encoded `full_name` param.
// Mirrors lib/repos: round-trips over `apiFetch` with the session bearer. The
// data shapes live in `./repo-detail-types` (dashboard 150-LOC cap).

import { type RepoPolicyWire, RepoPolicyWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { ApiError, apiFetch, withCursor } from './api';
import { type Page, type PagedList, usePagedQuery } from './paged';
import type {
  RepoDetailRow,
  RepoOverview,
  RepoPrRow,
  RepoSessionsPage,
  RepoSettings,
  SessionFilters,
} from './repo-detail-types';
import { SESSION_TOKEN } from './session';

/** One PR by its own id — `usePr` / `useReviewRuns` / `decidePending` (#1036). */
export * from './pr-detail';
export type * from './repo-detail-types';

/**
 * Route param → API path segment. The router carries `owner%2Frepo` in one
 * param; the API wants `/:owner/:repo`.
 */
export function repoApiBase(encodedFullName: string): string {
  const decoded = safeDecode(encodedFullName);
  const [owner = '', ...rest] = decoded.split('/');
  return `/v1/repos/${encodeURIComponent(owner)}/${encodeURIComponent(rest.join('/'))}`;
}

export function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export const REPO_DETAIL_QUERY_KEY = 'repo-detail';

export function useRepo(encoded: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<RepoDetailRow>(() => ({
    queryKey: [REPO_DETAIL_QUERY_KEY, encoded(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<RepoDetailRow>(repoApiBase(encoded()), token),
  }));
}

/**
 * The EFFECTIVE policy and why it is not the repo's own when it is not (#3911):
 * `fallback` set means the snapshot is broken and `policy` is the defaults the
 * platform runs. A 404 is a real state ("no snapshot yet"), not an error — it
 * resolves to null so the tab renders the missing-policy view.
 */
export function useRepoPolicy(encoded: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<RepoPolicyWire | null>(() => ({
    queryKey: [REPO_DETAIL_QUERY_KEY, encoded(), 'policy', token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () => {
      try {
        return await apiFetch(`${repoApiBase(encoded())}/policy`, token, undefined, {
          schema: RepoPolicyWireSchema,
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  }));
}

/**
 * The repo's PENDING merge decisions: open PRs stamped `merge_decision: ask`,
 * filtered by the SERVER and paged (#5608 slice 17 row 2). This was every PR in
 * every state, page one only, filtered client-side: a merge or close leaves the
 * stamp, so a settled PR rendered live Approve / Skip, and an ask older than the
 * newest page never rendered. The Pending tab, its badge and the Overview stat
 * all read this one list; `hasMore()` says the count in hand is not the total.
 */
export function useRepoPendingPrs(
  encoded: () => string,
  token: string | null = SESSION_TOKEN,
): PagedList<RepoPrRow> {
  return usePagedQuery<RepoPrRow>((cursor) =>
    createQuery<Page<RepoPrRow>>(() => {
      // The URL IS the key: filter and cursor can never drift apart in the cache.
      const at = cursor();
      const path = withCursor(`${repoApiBase(encoded())}/prs?${PENDING_PRS}`, at);
      return {
        queryKey: [REPO_DETAIL_QUERY_KEY, path, token, activeAccountId()],
        enabled: token !== null,
        queryFn: () => apiFetch<Page<RepoPrRow>>(path, token),
      };
    }),
  );
}

/** `state:open` AND `merge_decision:ask` — the shared grammar ANDs repeated filters. */
const PENDING_PRS = new URLSearchParams([
  ['filter', 'state:open'],
  ['filter', 'merge_decision:ask'],
]).toString();

export function useRepoOverview(encoded: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<RepoOverview>(() => ({
    queryKey: [REPO_DETAIL_QUERY_KEY, encoded(), 'overview', token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<RepoOverview>(`${repoApiBase(encoded())}/overview`, token),
  }));
}

/**
 * One page of the repo's agent sessions for the Audit tab (#349) — the shared
 * list grammar (`filter=trigger:…`/`outcome:…`, default `sort=-started_at`),
 * cursor-paginated. `filters`/`cursor` are reactive so a filter change or a
 * load-more re-runs the query; `''` on a filter axis omits it (any).
 */
export function useRepoSessions(
  encoded: () => string,
  filters: () => SessionFilters,
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<RepoSessionsPage>(() => ({
    queryKey: [
      REPO_DETAIL_QUERY_KEY,
      encoded(),
      'sessions',
      filters().trigger,
      filters().outcome,
      cursor(),
      token,
      activeAccountId(),
    ],
    enabled: token !== null,
    queryFn: () =>
      apiFetch<RepoSessionsPage>(
        `${repoApiBase(encoded())}/sessions?${sessionListParams(filters(), cursor())}`,
        token,
      ),
  }));
}

/** Build the `per_page`/`filter`/`cursor` query string, omitting empty filters. */
function sessionListParams(f: SessionFilters, cursor: string | null): string {
  const params = new URLSearchParams({ per_page: '20' });
  if (f.trigger) params.append('filter', `trigger:${f.trigger}`);
  if (f.outcome) params.append('filter', `outcome:${f.outcome}`);
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export function useRepoSettings(encoded: () => string, token: string | null = SESSION_TOKEN) {
  return createQuery<RepoSettings>(() => ({
    queryKey: [REPO_DETAIL_QUERY_KEY, encoded(), 'settings', token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => apiFetch<RepoSettings>(`${repoApiBase(encoded())}/settings`, token),
  }));
}
